import { CallSplitRounded, PlaceOutlined, WarningAmberRounded } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { FC, memo, useMemo } from 'react';
import { courseDiffBadgeLabels } from 'src/components/dispatch/courseDiff';
import { quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';
import {
  activeInfoMetrics,
  BaseMetric,
  CARD_METRIC_LIMIT,
  cardMetrics,
  displayMetricLabel,
  isValueMetric,
} from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';
import { SUB_TEXT_COLOR, SUPPLEMENT_TEXT_COLOR } from 'src/components/dispatch/unassignedDisplay/unassignedTextColors';
import { DispatchCard as DispatchCardType } from 'src/entities/dispatch';
import { GeoAlertInfo } from 'src/utils/geoAlerts';
import { getInfoMetricValue } from 'src/utils/metricHelpers';

import { DisplayMetric } from './DisplayMetricToggle';

export type DispatchCardContentProps = {
  card: Extract<DispatchCardType, { type: 'delivery' }>;
  showDuplicate: boolean;
  routeDeviation: boolean;
  displayMetrics?: Set<DisplayMetric>;
  /**
   * 会社設定の任意項目名。情報行に任意項目を並べるかの判定にだけ使う。
   * セル表示（cardEntries）はキー自身が項目名を持つので、渡さなくても解決できる。
   */
  customInputFieldNames?: string[];
  geoAlert?: GeoAlertInfo;
};

/**
 * 数値指標の表示文字列。
 * ダイヤの土台 (fr-template) は本物の案件ではないため、数量・重量・体積を
 * すべて "--" 表示にする (route-planner サンプル準拠)。
 * 本日なし (no-delivery) も同様に値なし扱い。
 * 配車済み (matched) と D&D 配置直後 (template) は legRun 実値をそのまま表示する。
 * legRun に値が無い (null) ときは "--" を出す (cases × 係数の推定値は出さない)。
 */
function valueMetricTexts(card: Extract<DispatchCardType, { type: 'delivery' }>) {
  const isFrTemplate = card.status === 'fr-template';
  const isNoDelivery = card.status === 'no-delivery';
  const cases = isFrTemplate || isNoDelivery
    ? null
    : card.todayCases ?? card.cases ?? null;
  const kg = isFrTemplate || isNoDelivery ? null : card.weight ?? null;
  const m3 = isFrTemplate || isNoDelivery ? null : card.volume ?? null;
  return {
    quantity: cases !== null ? `${quantityValueText(cases)} cs` : '-- cs',
    weight: kg !== null ? `${weightValueText(kg)}kg` : '--kg',
    volume: m3 !== null ? `${volumeValueText(m3)}m³` : '--m³',
  };
}

function formatMetrics(card: Extract<DispatchCardType, { type: 'delivery' }>, metrics?: Set<DisplayMetric>) {
  const effectiveMetrics = metrics ?? new Set<DisplayMetric>(['quantity']);
  const texts = valueMetricTexts(card);
  return (['quantity', 'weight', 'volume'] as const)
    .filter((key) => effectiveMetrics.has(key))
    .map((key) => texts[key]);
}

/** セル表示の 1 件。数値指標もテキスト項目も同じ形で解決する */
function cardMetricEntry(key: BaseMetric, card: Extract<DispatchCardType, { type: 'delivery' }>) {
  const value = isValueMetric(key) ? valueMetricTexts(card)[key] : getInfoMetricValue(key, card);
  return { key, label: displayMetricLabel(key), value, numeric: isValueMetric(key) };
}

/** 「元：101-3」「移動先：6-2」のタグ */
const courseBadgeSx = {
  borderRadius: '2px',
  bgcolor: 'hsl(var(--status-warning-accent) / 0.14)',
  px: 0.5,
  py: '2px',
  fontSize: 9,
  fontWeight: 600,
  lineHeight: 1,
  color: 'hsl(var(--status-warning-foreground))',
} as const;

function getStatusLabel(card: Extract<DispatchCardType, { type: 'delivery' }>) {
  // fr-template (ダイヤの土台) と template (D&D 配置後 timeline 未反映) はどちらも
  // 「未反映」表示で揃える (route-planner サンプル準拠)。
  if (card.status === 'fr-template' && card.needsReviewCourseCodes?.length) return '要確認';
  if (card.status === 'template' || card.status === 'fr-template') return '未反映';
  // ほかの行へ移した店舗は「本日なし」ではなく移動先のタグを出す（PM フィードバック 2026-10-27）
  if (card.status === 'no-delivery') return card.movedOutLegRunIds?.length ? null : '本日なし';
  return null;
}

export const DispatchCardContent: FC<DispatchCardContentProps> = memo(({
  card, showDuplicate, routeDeviation, displayMetrics, customInputFieldNames, geoAlert,
}) => {
  // isTemplate（highlightSx 用）はラッパ側の外側 Box の sx 計算にしか使わないため、
  // DispatchCardView.tsx に残している（このコンポーネントの JSX では未使用）。
  const isNoDelivery = card.status === 'no-delivery';
  const statusLabel = getStatusLabel(card);

  // 固定コースとの差分（log_web_issues#353）は matched のカードだけ（本日なし・未反映・要確認には出さない）。
  // ダイヤ外も同じ並びに入れ、元のコースと重なるときは 1 つにまとめる（log_web_issues#401）
  const routeDeviationShown = routeDeviation && !isNoDelivery;
  const courseDiffLabels = useMemo(
    () => courseDiffBadgeLabels(card.status === 'matched' ? card.courseDiffs ?? [] : [], routeDeviationShown),
    [card.status, card.courseDiffs, routeDeviationShown],
  );

  // 関数名 activeInfoMetrics（displayMetricKeys の import）と衝突するのでローカル変数は
  // cardInfoMetrics に改名する（同名 const は TDZ 参照エラーになる）。
  const cardInfoMetrics = useMemo(() => {
    if (!displayMetrics) return [];
    return activeInfoMetrics([...displayMetrics], customInputFieldNames ?? []);
  }, [displayMetrics, customInputFieldNames]);

  const infoText = useMemo(() => {
    if (cardInfoMetrics.length === 0) return '';
    return cardInfoMetrics
      .map((m) => getInfoMetricValue(m, card))
      .filter(Boolean)
      .join(' / ');
  }, [card, cardInfoMetrics]);

  // セル表示に指定された項目（最大 CARD_METRIC_LIMIT 件）。保存値が上限を超えていても
  // ここで切る（別画面や旧バージョンが書いた保存値でカードが伸びないように）。
  const cardEntries = useMemo(() => {
    if (!displayMetrics) return [];
    return cardMetrics([...displayMetrics])
      .slice(0, CARD_METRIC_LIMIT)
      .map((key) => cardMetricEntry(key, card))
      .filter((entry) => entry.value);
  }, [displayMetrics, card]);

  const hasInfoText = infoText.length > 0;
  const hasValueMetrics = formatMetrics(card, displayMetrics).length > 0;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1, '& > * + *': { mt: 0.5 } }}>
          {(showDuplicate || courseDiffLabels.length > 0 || card.movedOutLabel) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
              {card.movedOutLabel && (
                <Box component="span" data-testid="moved-out-badge" sx={courseBadgeSx}>
                  {card.movedOutLabel}
                </Box>
              )}
              {courseDiffLabels.map((label) => (
                <Box key={label} component="span" data-testid="course-diff-badge" sx={courseBadgeSx}>
                  {label}
                </Box>
              ))}
              {showDuplicate && !isNoDelivery && (
                <Box
                  component="span"
                  sx={{
                    borderRadius: '2px',
                    bgcolor: 'hsl(var(--primary) / 0.12)',
                    px: 0.5,
                    py: '2px',
                    fontSize: 9,
                    fontWeight: 600,
                    lineHeight: 1,
                    color: 'hsl(var(--primary))',
                  }}
                >
                  他車両での取扱あり
                </Box>
              )}
            </Box>
          )}
          <Box
            component="span"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              color: isNoDelivery ? 'hsl(var(--card-inactive-foreground))' : 'hsl(var(--card-foreground))',
            }}
          >
            {card.storeName}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'flex-start', gap: '2px' }}>
          {card.wasSplit && (
            <Tooltip title="分割された案件です" placement="top" enterDelay={120}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex', height: 16, width: 16,
                  alignItems: 'center', justifyContent: 'center',
                  color: 'hsl(var(--status-error-accent))',
                }}
              >
                <CallSplitRounded sx={{ fontSize: 12 }} />
              </Box>
            </Tooltip>
          )}
          {geoAlert && !isNoDelivery && (
            <Tooltip title={<Box component="span" sx={{ fontSize: 12, lineHeight: 1.6 }}>{geoAlert.message}</Box>} placement="top" enterDelay={120}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  height: 16,
                  width: 16,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '2px',
                  color: geoAlert.level === 'strong' ? 'hsl(var(--status-error-accent))' : 'hsl(var(--status-warning-accent))',
                }}
              >
                <PlaceOutlined sx={{ fontSize: 12 }} />
              </Box>
            </Tooltip>
          )}
          {card.hasAlert && !isNoDelivery && (
            <Tooltip title={<Box component="span" sx={{ fontSize: 12, lineHeight: 1.6 }}>指定時間が超過しています。</Box>} placement="top" enterDelay={120}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  height: 16,
                  width: 16,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '2px',
                  color: 'hsl(var(--status-error-accent))',
                }}
              >
                <WarningAmberRounded sx={{ fontSize: 12 }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      {cardEntries.length > 0 ? (
        /* セル表示。ラベル付きの行にすることで、値だけでは何の項目か読めない
           （備考と品名のように文字列が並ぶ項目）でも判別できる */
        <Box sx={{ mt: 0.5, display: 'grid', rowGap: '1px' }}>
          {cardEntries.map((entry) => (
            <Box key={entry.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Box
                component="span"
                sx={{
                  flexShrink: 0,
                  px: '3px',
                  borderRadius: '3px',
                  bgcolor: 'hsl(var(--secondary) / 0.8)',
                  color: SUPPLEMENT_TEXT_COLOR,
                  fontSize: 9,
                  fontWeight: 700,
                  lineHeight: 1.5,
                  maxWidth: 54,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {entry.label}
              </Box>
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 11,
                  lineHeight: 1.35,
                  fontWeight: entry.numeric ? 600 : 500,
                  textAlign: entry.numeric ? 'right' : 'left',
                  fontFamily: entry.numeric ? 'monospace' : undefined,
                  fontVariantNumeric: entry.numeric ? 'tabular-nums' : undefined,
                  color: isNoDelivery ? 'hsl(var(--card-inactive-foreground))' : SUB_TEXT_COLOR,
                }}
              >
                {entry.value}
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <>
          {hasValueMetrics && (
            <Box
              sx={{
                mt: 0.5,
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 1,
                fontSize: 10,
                fontFamily: 'monospace',
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
                // 未割当リストと同じ黒系にする（muted-foreground は白背景で 4.76 まで落ちて読めない）
                color: isNoDelivery ? 'hsl(var(--card-inactive-foreground))' : SUB_TEXT_COLOR,
              }}
            >
              {formatMetrics(card, displayMetrics).map((m, i) => (
                <Box component="span" key={i}>
                  {m}
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ mt: '2px', display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 9, lineHeight: 1.2 }}>
            {statusLabel && !hasInfoText && (
              <Box
                component="span"
                sx={{ color: isNoDelivery ? 'hsl(var(--card-inactive-foreground))' : SUB_TEXT_COLOR }}
              >
                {statusLabel}
              </Box>
            )}
            {hasInfoText && (
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  color: isNoDelivery ? 'hsl(var(--card-inactive-foreground))' : SUPPLEMENT_TEXT_COLOR,
                }}
              >
                {infoText}
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
});

DispatchCardContent.displayName = 'DispatchCardContent';
